
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";

const Blog = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-x-hidden">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 md:p-12">
          <article>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Trust from Afar: How ZimKonnect Empowers Zimbabwe's Diaspora Community
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
              Building a future in Zimbabwe from thousands of miles away no longer has to feel like a leap of faith. 
              Here's how ZimKonnect transforms uncertainty into confidence for diaspora families.
            </p>

            <div className="prose prose-sm sm:prose-lg max-w-none">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">
                The Silent Struggle: When Distance Breeds Doubt
              </h2>
              
              <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                Every month, millions of diaspora Zimbabweans send money home with a mixture of hope and worry. 
                Will the house foundation actually be laid? Is Grandmother's medical treatment progressing? 
                Are the school fees being used as intended? The emotional toll of not knowing can be overwhelming.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                For too long, supporting family and building dreams back home has meant sending money into the unknown. 
                Phone calls provide some reassurance, but they can't show you the reality. Photos arrive sporadically, 
                and you're left wondering: Is this really what my hard-earned money is achieving?
              </p>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">
                A New Way Forward: Transparency That Transforms Trust
              </h2>

              <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                ZimKonnect was born from a simple belief: families deserve to stay connected, no matter the distance. 
                Our platform bridges continents by giving you real-time visibility into every project, every milestone, 
                every step forward that your support makes possible.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                Imagine receiving a notification on your phone in London showing the exact moment the roof goes on your 
                family home in Harare. Picture getting verified updates about your grandmother's treatment from the clinic 
                in Bulawayo. This isn't just project tracking – it's peace of mind delivered to your doorstep.
              </p>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">
                What Real Transparency Looks Like
              </h2>

              <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 sm:p-6 rounded-lg border border-blue-200 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">With ZimKonnect, you get:</h3>
                <ul className="list-disc pl-4 sm:pl-6 text-gray-700 space-y-2">
                  <li><strong>Live Progress Updates:</strong> See your projects unfold in real-time with verified photos and milestone reports</li>
                  <li><strong>Trusted Local Partners:</strong> Connect with verified agents who become your eyes and hands on the ground</li>
                  <li><strong>Clear Communication:</strong> Secure messaging that keeps you in direct contact with your project team</li>
                  <li><strong>Financial Clarity:</strong> Know exactly where every dollar goes with detailed, itemized progress reports</li>
                  <li><strong>Quality Assurance:</strong> Get professional assessments to ensure your investments meet your standards</li>
                </ul>
              </div>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">
                Stories of Connection and Empowerment
              </h2>

              <blockquote className="border-l-4 border-amber-500 pl-4 sm:pl-6 mb-6 italic text-gray-700 bg-amber-50 py-4 pr-4 rounded-r-lg">
                "For the first time in 15 years of sending money home, I could watch my mother's house being built brick by brick from my flat in Toronto. The relief was indescribable – finally, I wasn't just hoping my money was being used well, I could see it happening."
                <footer className="text-sm text-gray-600 mt-2 not-italic">— Sarah M., Nurse in Canada</footer>
              </blockquote>

              <blockquote className="border-l-4 border-green-500 pl-4 sm:pl-6 mb-6 italic text-gray-700 bg-green-50 py-4 pr-4 rounded-r-lg">
                "When my father needed urgent medical care, ZimKonnect helped me stay informed every step of the way. I could see the treatment progress, speak directly with the care team, and ensure he was getting exactly what he needed. It brought our family closer despite the distance."
                <footer className="text-sm text-gray-600 mt-2 not-italic">— David K., Engineer in UK</footer>
              </blockquote>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">
                Beyond Money: Building Emotional Bridges
              </h2>

              <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                ZimKonnect understands that this isn't just about financial transactions – it's about family, dreams, 
                and the deep desire to contribute meaningfully to the lives of those you love. Our platform doesn't 
                just track projects; it strengthens the bonds that distance tries to weaken.
              </p>

              <p className="text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                When you can see your support making a real difference, when you can follow progress as it happens, 
                when you can communicate directly with the people making your dreams reality – that's when sending 
                money home transforms from an act of faith into an act of partnership.
              </p>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4">
                Your Journey Starts Here
              </h2>

              <p className="text-gray-700 mb-6 sm:mb-8 leading-relaxed">
                Whether you're building a home, supporting education, funding healthcare, or developing land, 
                ZimKonnect gives you the transparency and connection you deserve. It's time to transform how 
                diaspora families stay connected with home – with confidence, clarity, and genuine peace of mind.
              </p>

              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 sm:p-6 rounded-lg border border-amber-200 mt-6 sm:mt-8">
                <p className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Ready to build trust from afar?
                </p>
                <p className="text-gray-700 mb-4">
                  Join thousands of diaspora families who've discovered the peace of mind that comes with real transparency.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Link 
                    to="/waitlist" 
                    className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-amber-700 hover:to-orange-700 transition-all duration-200 font-semibold text-center min-h-[48px] flex items-center justify-center touch-manipulation"
                  >
                    Join the Waitlist
                  </Link>
                  <Link 
                    to="/how-it-works" 
                    className="border border-amber-600 text-amber-600 px-4 sm:px-6 py-3 rounded-lg hover:bg-amber-50 transition-all duration-200 font-semibold text-center min-h-[48px] flex items-center justify-center touch-manipulation"
                  >
                    See How We Build Trust
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
